import React, { useState } from 'react';

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  message: string;
}

const Contact: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    const messageLines = [
      `Hello MarketMate,`,
      ``,
      `Full Name: ${formData.fullName.trim()}`,
      `Email: ${formData.email.trim()}`,
    ];
    if (formData.phone.trim()) {
      messageLines.push(`Phone: ${formData.phone.trim()}`);
    }
    messageLines.push(``, `Message:`, formData.message.trim());

    const messageText = messageLines.join('\n');
    const whatsappUrl = `https://wa.me/2348112560962?text=${encodeURIComponent(messageText)}`;

    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="w-full overflow-hidden pt-24">
      {/* Contact Hero Section */}
      <section className="relative w-full bg-gradient-to-b from-[#e8f5f0] via-[#f0faf7] to-[#f8fcfb] py-20 md:py-32 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-extrabold tracking-tight mb-6">
              <span className="text-emerald-700">Contact</span> <span className="text-orange-500">Us</span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-slate-700 leading-relaxed max-w-3xl mx-auto">
              Feel free to reach out to us with any question, feedback or inquiry.
            </p>
          </div>

          {/* Contact Details Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-16">
            <a
              href="https://wa.me/2348112560962"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-5 p-6 rounded-3xl bg-white border border-emerald-200 shadow-xl hover:shadow-2xl transition-all group hover:-translate-y-1"
            >
              <div className="w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center text-2xl flex-shrink-0 group-hover:scale-110 transition-transform">
                📞
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-orange-500">Call / WhatsApp</p>
                <p className="text-xl font-bold text-slate-900 mt-1">+234 811 256 0962</p>
                <p className="text-xs text-slate-600 mt-0.5">Click to chat on WhatsApp</p>
              </div>
            </a>

            <a
              href="mailto:support@marketmateconnectltd.com"
              className="flex items-center gap-5 p-6 rounded-3xl bg-white border border-emerald-200 shadow-xl hover:shadow-2xl transition-all group hover:-translate-y-1"
            >
              <div className="w-14 h-14 rounded-2xl bg-orange-100 text-orange-600 flex items-center justify-center text-2xl flex-shrink-0 group-hover:scale-110 transition-transform">
                📧
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-emerald-700">Support Email</p>
                <p className="text-lg font-bold text-slate-900 mt-1 break-all">support@marketmateconnectltd.com</p>
                <p className="text-xs text-slate-600 mt-0.5">Send us an email anytime</p>
              </div>
            </a>
          </div>

          <div className="flex justify-center">
            <div className="w-full max-w-5xl">
              <img
                src="/images/Conatctsec1.png"
                alt="Contact Support - Customer Service"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="relative w-full bg-gradient-to-b from-white via-[#f0faf7] to-[#e8f5f0] py-20 md:py-32 overflow-hidden">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight text-emerald-700 text-center mb-4">
            Fill Form
          </h2>
          <p className="text-center text-slate-600 mb-12 text-sm sm:text-base">
            Submitting this form will open a pre-filled WhatsApp message directly to our team at <span className="font-semibold text-emerald-700">+234 811 256 0962</span>.
          </p>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Full Name */}
            <div>
              <label htmlFor="fullName" className="block text-base sm:text-lg font-semibold text-slate-900 mb-3">
                Full Name
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                required
                className="w-full px-5 sm:px-6 py-3 sm:py-4 rounded-2xl border-2 border-emerald-700 bg-[#e8f5f0] text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-700 focus:ring-offset-2 transition-all text-base sm:text-lg"
                placeholder="Enter your full name"
              />
            </div>

            {/* Email Address */}
            <div>
              <label htmlFor="email" className="block text-base sm:text-lg font-semibold text-slate-900 mb-3">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-5 sm:px-6 py-3 sm:py-4 rounded-2xl border-2 border-emerald-700 bg-[#e8f5f0] text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-700 focus:ring-offset-2 transition-all text-base sm:text-lg"
                placeholder="Enter your email address"
              />
            </div>

            {/* Phone Number */}
            <div>
              <label htmlFor="phone" className="block text-base sm:text-lg font-semibold text-slate-900 mb-3">
                Phone Number <span className="font-normal text-slate-600">(Optional)</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-5 sm:px-6 py-3 sm:py-4 rounded-2xl border-2 border-emerald-700 bg-[#e8f5f0] text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-700 focus:ring-offset-2 transition-all text-base sm:text-lg"
                placeholder="Enter your phone number"
              />
            </div>

            {/* Message */}
            <div>
              <label htmlFor="message" className="block text-base sm:text-lg font-semibold text-slate-900 mb-3">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={6}
                className="w-full px-5 sm:px-6 py-3 sm:py-4 rounded-2xl border-2 border-emerald-700 bg-[#e8f5f0] text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-700 focus:ring-offset-2 transition-all text-base sm:text-lg resize-none"
                placeholder="Enter your message"
              ></textarea>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                className="w-full sm:w-auto px-8 sm:px-10 py-3 sm:py-4 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-2xl transition-colors text-base sm:text-lg shadow-lg shadow-orange-500/30 flex items-center justify-center gap-2 cursor-pointer"
              >
                Send via WhatsApp <span className="ml-1">💬 →</span>
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Section Three - Join the Team */}
      <section className="relative w-full bg-gradient-to-b from-[#e8f5f0] via-[#e8f5f0] to-[#f0faf7] overflow-hidden">
        {/* Image Section */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24 pb-12">
          <div className="flex justify-center">
            <div className="w-full max-w-5xl">
              <img
                src="/images/Aboutsec4.png"
                alt="Join MarketMate Team - Vendor, Customer, Rider"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>

        {/* CTA Card Section */}
        <div className="flex justify-center pb-20 md:pb-32 px-4 sm:px-6 lg:px-8">
          <div className="w-full max-w-2xl bg-orange-500 rounded-3xl p-8 sm:p-12 text-center shadow-2xl shadow-orange-500/30">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white mb-6">
              Join the MarketMate Team
            </h2>
            <p className="text-base sm:text-lg text-white/95 leading-relaxed mb-10">
              Help us make fresh food more accessible to everyone. If you're passionate about building, solving real problems, and creating impact, we'd love to have you on the journey.
            </p>
            <button className="inline-block bg-emerald-700 hover:bg-emerald-800 text-white font-bold py-3 sm:py-4 px-8 sm:px-10 rounded-full transition-colors text-base sm:text-lg shadow-lg">
              View Open Roles <span className="ml-2">→</span>
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
